import { withProps } from 'recompose';
import { createEnum } from '../../core/createEnum';

/**
 * @typedef {Object} PermissionEntryType
 * @property {string} label
 * @property {boolean} isAdminPermission
 * @property {(permissionList?: string[]) => boolean} isSetOn
 * @property {(disabled?: boolean) => {value: string, label: string, disabled: boolean}} toRigthOption
 */

class PermissionEntry {
  constructor(name, { label, isAdminPermission }) {
    this[this.nameSym] = this.symbolFor(name);
    this.label = label;
    this.isAdminPermission = isAdminPermission;

    this.isSetOn = (permissionList = []) =>
      permissionList.includes(this.nameOfSym(this[this.nameSym]));

    this.toRigthOption = (disabled = false) => ({
      value: this.nameOfSym(this[this.nameSym]),
      label: this.label,
      disabled: disabled || this.isAdminPermission,
    });

    Object.freeze(this);
  }
}

PermissionEntry.prototype.nameSym = Symbol('PermissionEntry.name');
PermissionEntry.prototype.symbolFor = (name) =>
  Symbol.for('com.tikeasy.' + name);
PermissionEntry.prototype.nameOfSym = (sym) => {
  const fullName = Symbol.keyFor(sym);
  return fullName.substr((~-fullName.lastIndexOf('.') >>> 0) + 2);
};

class PermissionEnum {
  constructor(enumLiterals) {
    const _this = this;
    Object.keys(enumLiterals).forEach(
      (key) => (_this[key] = new PermissionEntry(key, enumLiterals[key])),
    );

    Object.freeze(this);
  }

  keys() {
    return Object.keys(this);
  }

  values() {
    let result = Array.from({ length: this.keys().length });
    const _this = this;

    this.keys().forEach((key) => result.push(_this[key]));

    return result;
  }

  /** @param {(entry: any) => boolean} filter */
  _filteredRightOptions(filter = () => true) {
    let result = [];
    const _this = this;

    this.keys().forEach((key) =>
      result.push(_this[key].toRigthOption(!filter(_this[key]))),
    );

    return result;
  }

  get rightOptions() {
    return this._filteredRightOptions();
  }

  filteredRightOptions(permissions) {
    let filter = (permissionEntry) => permissionEntry.isSetOn(permissions);

    return this._filteredRightOptions(filter);
  }

  labelOf(permissionName, orElse = '') {
    return this.hasOwnProperty(permissionName)
      ? this[permissionName].label
      : orElse;
  }
}

/**
 * @template {Record<string, {label: string, isAdminPermission: boolean}>} T
 * @param {T} enumLiterals
 * @returns {Readonly<Record<keyof T, PermissionEntryType>> & PermissionEnum}
 */
function createPermissionEnum(enumLiterals) {
  return /** @type {Readonly<Record<keyof T, PermissionEntryType>> & PermissionEnum} */ (
    createEnum(PermissionEnum, enumLiterals)
  );
}

const ADMIN_ROLE_NAME = 'admin';

const Permissions = createPermissionEnum({
  MANAGE_USERS: { label: 'Gérer les utilisateurs', isAdminPermission: false },
  VISUALIZE_USERS: {
    label: 'Visualiser les utilisateurs',
    isAdminPermission: false,
  },
  MANAGE_CONTENTS: { label: 'Gérer les contenus', isAdminPermission: false },
  MANAGE_CONTENT_IMPORTS: {
    label: 'Importer des contenus',
    isAdminPermission: false,
  },
  VISUALIZE_CONTENTS: {
    label: 'Visualiser les contenus',
    isAdminPermission: false,
  },
  MANAGE_FLEET: { label: 'Gérer le parc', isAdminPermission: false },
  VISUALIZE_FLEET: { label: 'Visualiser le parc', isAdminPermission: false },
  MANAGE_PARTNER_ACCOUNTS: {
    label: 'Gérer les comptes partenaires',
    isAdminPermission: true,
  },
  VISUALIZE_PARTNER_ACCOUNTS: {
    label: 'Visualiser les comptes partenaires',
    isAdminPermission: true,
  },
  MANAGE_PARTNER_CHANNELS: {
    label: 'Gérer les canaux partenaires',
    isAdminPermission: false,
  },
  VISUALIZE_PARTNER_CHANNELS: {
    label: 'Visualiser les canaux partenaires',
    isAdminPermission: false,
  },
  VISUALIZE_ADMIN: {
    label: 'Visualiser les administrateurs',
    isAdminPermission: true,
  },
});

const filterPartnerLink = (connectedUser) => {
  class FilterableLinks {
    constructor(links) {
      this.links = links;
    }

    filter(linkFilter) {
      return new FilterableLinks(this.links.filter(linkFilter));
    }

    byChannel(channel) {
      return this.filter(
        (link) => link.defaultPartnerChannel.channel === channel,
      );
    }

    byPartnerAccountId(partnerAccountId) {
      return this.filter((link) => link.partnerAccount.id === partnerAccountId);
    }

    byPartnerAccountNumber(partnerAccountNumber) {
      return this.filter(
        (link) => link.partnerAccount.partnerNumber === partnerAccountNumber,
      );
    }

    byPermission(permission) {
      return this.filter((link) => permission.isSetOn(link.defaultPermissions));
    }

    get get() {
      return this.links;
    }
  }

  const links =
    connectedUser !== undefined && connectedUser.links !== undefined
      ? connectedUser.links
      : [];

  return new FilterableLinks(links);
};

const USER_RIGHT_OPTIONS = [
  'MANAGE_USERS',
  'VISUALIZE_USERS',
  'MANAGE_CONTENTS',
  'MANAGE_CONTENT_IMPORTS',
  'VISUALIZE_CONTENTS',
  'MANAGE_FLEET',
  'VISUALIZE_FLEET',
];

const withPermissions = withProps(({ isAdmin, selectedLink }) => ({
  permissions: Permissions.rightOptions.filter(
    (right) =>
      USER_RIGHT_OPTIONS.includes(right.value) &&
      (isAdmin ||
        selectedLink.defaultPermissions.includes(right.value) ||
        right.value === 'MANAGE_CONTENT_IMPORTS'),
  ),
}));

export { ADMIN_ROLE_NAME, Permissions, filterPartnerLink, withPermissions };
